import { Fragment } from 'react'

import { BiDoorOpen } from 'react-icons/bi'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  HStack,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react'

import {
  Camera,
  DeviceAssociation,
  Gateway,
  SecurityDevice,
} from '@/components/icons'
import { ClickToCopyButton } from '@/components/layouts/Copy/ClickToCopyButton'
import { Spinner } from '@/components/ui'
import { useDeviceMetaDataQuery } from '@/graphql/generated/hooks'

interface DeviceInfoTabIProps {
  id: string
}

interface LabeledFieldIProps {
  label: string
  value?: string | number | null
}

interface SectionHeaderIProps {
  icon: React.ElementType
  title: string
  iconSize?: string
}

interface SectionContainerIProps {
  children: React.ReactNode
}

const InfoField = ({ label, value }: LabeledFieldIProps) => {
  const displayValue = value?.toString() || ''

  return (
    <HStack spacing={2} width='100%'>
      <Text fontWeight='medium'>{label}:</Text>
      <Text fontFamily='mono'>{displayValue}</Text>
      {displayValue && <ClickToCopyButton textToCopy={displayValue} />}
    </HStack>
  )
}

const SectionHeader = ({
  icon,
  title,
  iconSize = '24px',
}: SectionHeaderIProps) => (
  <HStack paddingTop='2' spacing='2'>
    <Icon as={icon} boxSize={iconSize} strokeColor='black' />
    <Text fontWeight='bold'>{title}</Text>
  </HStack>
)

const SectionContainer = ({ children }: SectionContainerIProps) => (
  <VStack
    align='start'
    borderColor='#BBB'
    borderRadius='md'
    borderWidth='1px'
    padding='3'
    width='100%'
  >
    {children}
  </VStack>
)

export const DeviceInfoTab = ({ id }: DeviceInfoTabIProps) => {
  const { data, loading: isLoading } = useDeviceMetaDataQuery({
    variables: { id: id },
  })

  const device = data?.device
  const gateway = device?.edgeDevice
  const tailgateConnections = device?.tailgateConns?.edges

  return isLoading ? (
    <Box alignItems='center' height='100%' width='100%'>
      <Spinner />
    </Box>
  ) : (
    <VStack
      alignItems='start'
      marginTop={5}
      paddingBottom='10'
      paddingX={10 / 2}
      spacing='2'
      width='100%'
    >
      <Alert borderRadius='md' status='info'>
        <HStack spacing={2}>
          <AlertIcon />
          <VStack alignItems='start' spacing={0}>
            <AlertDescription>Only visible to Implementers</AlertDescription>
          </VStack>
        </HStack>
      </Alert>

      {/* region Device */}
      <SectionHeader icon={SecurityDevice} title='Device' />

      <SectionContainer>
        <InfoField label='Device ID' value={id} />
        <InfoField label='Created at' value={device?.createdAt} />
        <InfoField label='Updated at' value={device?.updatedAt} />

        <HStack spacing='2'>
          <Text fontWeight='medium'>Is placed?</Text>
          <Text fontFamily='mono'>{`${device?.isPlaced ?? false}`}</Text>
        </HStack>
      </SectionContainer>
      {/* endregion */}

      {/* region Camera */}
      {device?.camera && (
        <>
          <SectionHeader icon={Camera} title='Camera' />

          <SectionContainer>
            <InfoField
              label='Stream width'
              value={device?.camera?.streamingResolutionWidth}
            />
            <InfoField
              label='Stream height'
              value={device?.camera?.streamingResolutionHeight}
            />
            <InfoField label='Created at' value={device?.camera?.createdAt} />
            <InfoField label='Updated at' value={device?.camera?.updatedAt} />
          </SectionContainer>
        </>
      )}
      {/* endregion */}

      {/* region Door */}
      {device?.door && (
        <>
          <HStack paddingTop='2' spacing='2' width='100%'>
            <BiDoorOpen color='black' size={24} />
            <Text fontWeight='bold' width='100%'>
              Door
            </Text>
          </HStack>

          <SectionContainer>
            <InfoField label='External ID' value={device?.door?.externalId} />
            <InfoField label='Created at' value={device?.door?.createdAt} />
            <InfoField label='Updated at' value={device?.door?.updatedAt} />
          </SectionContainer>
        </>
      )}
      {/* endregion */}

      {/* region Gateway */}
      {device?.camera && (
        <>
          <SectionHeader icon={Gateway} title='Gateway' />

          <SectionContainer>
            {gateway?.id || gateway?.name ? (
              <>
                <InfoField label='Gateway ID' value={gateway?.id} />
                <InfoField label='Gateway Name' value={gateway?.name} />
              </>
            ) : (
              <Text>Not connected</Text>
            )}
          </SectionContainer>
        </>
      )}
      {/* endregion */}

      {/* region Associations */}
      <HStack paddingTop='2' width='100%'>
        <DeviceAssociation />
        <Text fontWeight='bold' width='100%'>
          Associations
        </Text>
      </HStack>

      {tailgateConnections?.map((connection, index) => (
        <Fragment key={connection?.node?.id || index}>
          <Text paddingTop={index === 0 ? 0 : 4}>
            Device Association #{index + 1}
          </Text>

          <SectionContainer>
            <Text
              fontWeight='medium'
              paddingLeft='0'
              textDecoration='underline'
              width='100%'
            >
              Connected Device
            </Text>

            <InfoField
              label='Device Name'
              value={connection?.node?.door?.name}
            />
            <InfoField label='Device ID' value={connection?.node?.door?.id} />

            {connection?.node?.cameras?.edges?.map((camera, cameraIndex) => (
              <Fragment key={camera?.node?.id || cameraIndex}>
                <Text
                  fontWeight='medium'
                  paddingLeft='0'
                  paddingTop='4'
                  textDecoration='underline'
                  width='100%'
                >
                  Connected Camera #{cameraIndex + 1}
                </Text>
                <InfoField label='Camera Name' value={camera?.node?.name} />
                <InfoField label='Camera ID' value={camera?.node?.id} />
              </Fragment>
            ))}
          </SectionContainer>
        </Fragment>
      ))}
      {/* endregion */}
    </VStack>
  )
}
